import { type FinCategory, isAsset } from '#dictionary'
import type { FinCategoryKey } from '#dictionary/finCategories'
import type { Region } from '#dictionary/regions'
import type { Nullable } from '#domain/types'
import MemberService from '#profile/MemberService'

import projectValue from '../projectValue'
import type { TimelineableRecord } from './aggregateRecordsByYear'

export type YearsRange = [number, number]

export type FinancialRecordPayload = {
  id?: string
  name: string
  ownerId: string
  finCategoryKey: FinCategoryKey
  startAt: Date
  endAt: Date
  annualValue: number
  valuePart: number
  recurringValue: Nullable<number>
  customRate: Nullable<number>
  parentId: Nullable<string>
  realEstateRegion: Nullable<Region>
  realEstateTaxRate: Nullable<number>
  questId: Nullable<string>
  t_fromAssetId: Nullable<string>
  t_toAssetId: Nullable<string>
}

// TODO: #should decide and agree that record names are not changeable
// TODO: all the assets may have contributions in the future.
// Need an algorithm to calculate the contributions.
class FinancialRecord implements TimelineableRecord {
  public readonly id: string
  public readonly name: string
  public readonly parentId: Nullable<string>
  public readonly valuePart: number
  public readonly t_fromAssetId: Nullable<string>
  public readonly t_toAssetId: Nullable<string>
  public readonly finCategory: FinCategory
  public readonly owner: MemberService
  public readonly questId: Nullable<string>
  public readonly startAt: Date
  public readonly endAt: Date
  public readonly annualValue: number
  public readonly recurringValue: Nullable<number>

  protected readonly customGrowthRate: Nullable<number>

  constructor(
    payload: FinancialRecordPayload,
    finCategory: FinCategory,
    owner: MemberService
  ) {
    this.id = payload.id ?? crypto.randomUUID()
    this.name = payload.name
    this.startAt = payload.startAt
    this.endAt = payload.endAt
    this.customGrowthRate = payload.customRate
    this.parentId = payload.parentId
    this.annualValue = payload.annualValue
    this.recurringValue = payload.recurringValue
    this.valuePart = payload.valuePart
    this.finCategory = finCategory
    this.owner = owner
    this.questId = payload.questId
    this.t_fromAssetId = payload.t_fromAssetId
    this.t_toAssetId = payload.t_toAssetId
  }

  get growthRate(): number {
    return this.customGrowthRate ?? this.finCategory.rate
  }

  getGrowthRateForYear(_year: number): number {
    return this.growthRate
  }

  get actualYearsRange(): YearsRange {
    return [this.startYear, this.endAt.getFullYear()]
  }

  get timelineYearsRange(): YearsRange {
    const [actualStartYear, actualEndYear] = this.actualYearsRange
    const timelineEndYear = isAsset(this.finCategory)
      ? actualEndYear + 1
      : actualEndYear
    return [actualStartYear, timelineEndYear]
  }

  projectedValue(year: number): number {
    return projectValue(this.annualValue, this.growthRate, this.startYear, year)
  }

  get region(): Region {
    return this.owner.region
  }

  private get startYear(): number {
    return this.startAt.getFullYear()
  }
}

export default FinancialRecord
